import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate } from 'react-router';
import {
  BrandMark,
  Button,
  Dialog,
  Icon,
  Tag,
  ThemeToggle,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { homePath, visibleNav } from '@dayon/rbac';
import { ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ACCOUNT_MENU_ROUTES } from '../account/account-menu.ts';
import './shell.css';

const initials = (name: string) =>
  name
    .split(' ')
    .filter((w) => w.length > 1)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/**
 * The account menu.
 *
 * Written here rather than in the design system because it has exactly one
 * user. If a second console ever needs it, it moves — a shared primitive with
 * one caller is a guess about the future dressed up as reuse.
 *
 * The pattern is the button/menu one from WAI-ARIA APG:
 *   • the trigger carries `aria-haspopup="menu"` and `aria-expanded`, so it
 *     announces as a menu button whether it is open or shut;
 *   • Enter and Space open it (a real <button>, so the browser does that);
 *     ArrowDown/ArrowUp open it onto the first/last item;
 *   • inside, focus roves — exactly one item is tabbable, arrows and Home/End
 *     move it, and Tab walks the items and then LEAVES rather than trapping;
 *   • Escape closes and puts focus back on the trigger, which is the only way
 *     a keyboard user gets their place back;
 *   • a pointerdown anywhere else closes it. The listener is on the document
 *     rather than an invisible scrim: a scrim would swallow the click that
 *     dismissed the menu, so dismissing it would cost two clicks.
 */
function AccountMenu() {
  const { session, signOut, signOutEverywhere } = useSession();
  const navigate = useNavigate();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const uid = useId();
  const triggerId = `${uid}trigger`;
  const menuId = `${uid}menu`;

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  /*
   * Focus follows the roving index — running on `active` as well as `open` is
   * what makes the arrow keys move focus rather than only a highlight.
   *
   * The items are read out of the DOM rather than collected into a ref array.
   * A ref array means writing `ref.current[i]` from a ref callback in render,
   * and `role="menuitem"` is the item's own definition of itself, so querying
   * for it cannot drift out of step with what is rendered.
   */
  useEffect(() => {
    if (!open) return;
    popRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[active]?.focus();
  }, [open, active]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (popRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;
      // No focus restore: the user is already pointing somewhere else, and
      // yanking focus back to the trigger would fight them for it.
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  if (!session) return null;

  const openAt = (index: number) => {
    setActive(index);
    setOpen(true);
  };

  const failed = (title: string, message: string, error: unknown) => {
    showError({
      title,
      message,
      ...(error instanceof ApiRequestError ? { code: error.code } : {}),
    });
  };

  const runSignOut = () => {
    setOpen(false);
    // The endpoint clears the cookie, so a throw means it was never cleared:
    // the session is still live and the console still open. On a shared
    // terminal, silence here IS the exposure.
    void signOut()
      .then(() => toast('Signed out on this device.'))
      .catch((error: unknown) => {
        triggerRef.current?.focus();
        failed(
          'Could not sign out',
          'You are still signed in on this device. Check your connection and try again.',
          error,
        );
      });
  };

  const askSignOutEverywhere = () => {
    /* Focus goes back to the trigger BEFORE the dialog opens, so the dialog
       captures the trigger as the element to restore to. Restoring to a menu
       item that the same commit unmounted would strand focus on <body>. */
    setOpen(false);
    triggerRef.current?.focus();
    setConfirming(true);
  };

  const confirmSignOutEverywhere = () => {
    setBusy(true);
    void signOutEverywhere()
      .then(() => {
        /* The console unmounts on the line above — the session is gone. The
           toast provider is mounted ABOVE the session, so this is still on
           screen, over the sign-in page, which is the only place the user can
           actually read it. */
        toast('Signed out everywhere. Every other browser and device is signed out too.');
      })
      .catch((error: unknown) => {
        setBusy(false);
        setConfirming(false);
        failed(
          'Could not sign out everywhere',
          'Your other sessions may still be live. Nothing else was changed, and running it ' +
            'again is harmless — check your connection and try again.',
          error,
        );
      });
  };

  /*
   * THE MENU, and the account route sits at the top of it.
   *
   * `/account` is where the self-service credential controls live — password,
   * authenticator, recovery codes — and this menu is the only way to reach it.
   * `ACCOUNT_MENU_ROUTES` is read here and by `nav.test.ts`, so a route excused
   * from the sidebar's orphan check is one that is genuinely linked from here.
   *
   * It is first because it is the thing people come looking for, and the two
   * sign-out items are last because one of them is the most destructive control in
   * the console and should not sit under a cursor on its way somewhere else.
   */
  const items = [
    ...ACCOUNT_MENU_ROUTES.map((route) => ({
      key: route.path,
      icon: route.icon,
      label: route.label,
      note: route.note,
      danger: false,
    })),
    {
      key: 'this-device',
      icon: 'sign-out',
      label: 'Sign out',
      note: 'Ends this session, in this browser only.',
      danger: false,
    },
    {
      key: 'everywhere',
      icon: 'shield-warning',
      label: 'Sign out everywhere',
      note: 'Ends every session on your account, on every device.',
      danger: true,
    },
  ] as const;

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openAt(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(items.length - 1);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const last = items.length - 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (i === last ? 0 : i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i === 0 ? last : i - 1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(last);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      /* Tab walks the items too, and steps OUT at either end rather than
         trapping. preventDefault is required at the ends because closing the
         menu unmounts the focused item before the browser performs its own
         focus move, which would otherwise dump focus on <body>. */
      const leaving = event.shiftKey ? active === 0 : active === last;
      event.preventDefault();
      if (leaving) close();
      else setActive((i) => (event.shiftKey ? i - 1 : i + 1));
    }
  };

  /**
   * `session.brands` is the scope RESTRICTION, so an empty list means every
   * brand rather than none. Saying which it is here is the point of showing it.
   */
  const scope =
    session.brands.length > 0
      ? session.brands.map((b) => b.name).join(', ')
      : 'All brands (unrestricted)';

  return (
    <div className="account">
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className="account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        /* The visible name and role are the label, and they are hidden from
           assistive tech below 700px, so the accessible name is stated here and
           repeats them verbatim — WCAG 2.5.3 wants the visible words inside it. */
        aria-label={`Account menu for ${session.displayName}, ${session.role}`}
        onClick={() => (open ? close() : openAt(0))}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="avatar" aria-hidden="true">
          {initials(session.displayName)}
        </span>
        <span className="account-text" aria-hidden="true">
          <span className="account-name">{session.displayName}</span>
          <span className="account-role text-subtle">{session.role}</span>
        </span>
        <Icon name="caret-down" className="account-caret" />
      </button>

      {open ? (
        <div className="account-pop elev-lg" ref={popRef}>
          <div className="account-who">
            <div className="account-who-name">{session.displayName}</div>
            <div className="account-who-email text-subtle">{session.email}</div>
            <div className="account-who-tags">
              <Tag tone="accent">{session.role}</Tag>
            </div>
            <div className="account-who-scope text-subtle">Brand scope: {scope}</div>
          </div>

          <div
            className="account-menu"
            role="menu"
            id={menuId}
            aria-label={`Account actions for ${session.displayName}`}
            onKeyDown={onMenuKeyDown}
          >
            {items.map((item, i) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={item.danger ? 'account-item account-item-danger' : 'account-item'}
                tabIndex={active === i ? 0 : -1}
                /* The note is a DESCRIPTION, not part of the name: a menu item
                   called "Sign out Ends this session, in this browser only." is
                   not a thing anyone can ask for by voice. */
                aria-labelledby={`${uid}${item.key}-label`}
                aria-describedby={`${uid}${item.key}-note`}
                onClick={
                  item.key === 'everywhere'
                    ? askSignOutEverywhere
                    : item.key === 'this-device'
                      ? runSignOut
                      : () => {
                          close();
                          navigate(item.key);
                        }
                }
              >
                <Icon name={item.icon} className="account-item-icon" />
                <span className="account-item-text">
                  <span id={`${uid}${item.key}-label`} className="account-item-label">
                    {item.label}
                  </span>
                  <span id={`${uid}${item.key}-note`} className="account-item-note text-subtle">
                    {item.note}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Portalled to <body>: the header is a sticky stacking context at
          --z-header, and a dialog nested inside it cannot rise above the
          off-canvas nav drawer (above --z-drawer) no matter what z-index it
          asks for. Every other dialog in the app is mounted at the root
          already. */}
      {confirming
        ? createPortal(
            <Dialog
              open
              onClose={() => {
                if (!busy) setConfirming(false);
              }}
              title="Sign out everywhere?"
              /*
               * NOT `dismissible={!busy}`, which is the shape this was nearly
               * written in and the shape eleven other confirmations in the
               * console shipped with. That guard blocks Escape only WHILE THE
               * WRITE IS IN FLIGHT — the one moment the decision has already
               * been made and a keystroke can no longer do any harm — and
               * leaves the dialog freely dismissible before the press, which
               * is the moment that matters.
               *
               * Here the guard was missing altogether, and the cost of that is
               * specific rather than theoretical: the operator arrives at this
               * dialog from a MENU they have just dismissed with Escape, so
               * the key is already under their finger, and a second press
               * threw away the decision with nothing on screen to say it had
               * been dropped. Somebody who opened this because they think
               * another person has their account would have been left
               * believing they had acted.
               *
               * The two ways out are Cancel and Sign out everywhere, and both
               * of them are an answer.
               */
              dismissible={false}
              actions={
                <>
                  <Button onClick={() => setConfirming(false)} disabled={busy}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    busy={busy}
                    /* The consequence is read out at the button as well as on
                       the way down the dialog. A sighted operator passes the
                       banner on their way to the press; somebody on a screen
                       reader can arrive at the button from the title alone,
                       and the title only asks the question. */
                    aria-describedby={`${uid}everywhere-consequence`}
                    onClick={confirmSignOutEverywhere}
                  >
                    Sign out everywhere
                  </Button>
                </>
              }
            >
              <div className="stack">
                {/* ── DANGER AND NOT WARN, AND THE CHOICE WAS READ OFF THE ROUTE ──
                    The tone contract beside `.notice-danger` in the design system
                    gives warn the reversible consequence and keeps danger for the
                    irreversible, or for anything that moves money or a reported
                    figure. No money moves here, so it is the first clause doing
                    all the work — and it is met three times over.

                    What the press actually does: it raises the account's token
                    version, which every request is checked against, closes the
                    record of every live session, and revokes every device the
                    operator has marked as trusted. Nothing restores any of the
                    three. There is no route that puts a session back, and the
                    trust cannot be re-granted except by signing in again on each
                    browser and marking it afresh.

                    It is also the one destructive control in this console whose
                    subject is the person pressing it. Every other red button in
                    the console is aimed at a player, a partner or a window; this
                    one signs the operator out of the browser they are sitting in,
                    and if they cannot produce a second factor afterwards they do
                    not get back in without help from another administrator. That
                    is the worst outcome available from any single press in here,
                    which is exactly what the red is for.

                    ── THE PROSE WAS MOVED, AND ONE SENTENCE OF IT WAS WRONG ──
                    The first sentence below was already here as the opening
                    paragraph, in these words. It has been MOVED rather than
                    copied — two copies of one sentence are one copy too many to
                    keep true — and the paragraphs that are not the consequence
                    stay underneath as paragraphs.

                    The sentence that did NOT survive the move promised that
                    "devices you have marked as trusted stay trusted — this ends
                    their sessions, it does not undo the trust". That stopped
                    being true: the route now revokes `identity.device_trust`
                    alongside the sessions, because a trusted browser SKIPS the
                    second factor, and somebody pressing this because they fear
                    another person has their account was handed back a browser
                    that needed nothing but the password. The reassurance was
                    never tested against the route and nothing failed when the
                    route changed under it — so the screen went on telling the
                    operator the opposite of what the platform does.

                    Saying that the trust goes too is not a smaller promise than
                    the one it replaces, it is the larger one: it tells the
                    operator they will need their authenticator app to get back
                    in, which is the thing they would otherwise discover from a
                    sign-in screen with their phone in another room. */}
                <div id={`${uid}everywhere-consequence`} className="notice notice-danger">
                  <Icon name="warning" />
                  <div>
                    Every session on <strong>your own account</strong> ends immediately — this
                    browser, every other browser, and every other device you are signed in on,
                    including any device you have signed in on and left trusted. The trust goes with
                    them: any browser you had marked as trusted will ask for a code from your
                    authenticator app again, so have the app — or one of your recovery codes — to
                    hand before you press this. <strong>This cannot be undone.</strong>
                  </div>
                </div>

                <p className="account-confirm-line">
                  You will have to sign in again everywhere, starting here. Nobody else is signed
                  out: this only affects your sessions, not your colleagues&rsquo;.
                </p>
                <p className="account-confirm-line">
                  If you only want to leave this browser, use
                  <strong> Sign out</strong> instead.
                </p>
              </div>
            </Dialog>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * The focusable descendants of a container, in DOM order.
 *
 * `:not([disabled])` and the `tabindex="-1"` exclusion are the whole of it —
 * a disabled control and a programmatically-focusable one are both things Tab
 * skips, and a trap that wraps to one of them strands the user on something
 * they cannot act on. `offsetParent` rejects anything display:none; the drawer
 * hides its labels at rail width, so this is not hypothetical.
 */
function focusables(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  const sel =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(sel)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * The console shell: sidebar, header, content.
 *
 * Navigation is filtered by capability — a module the principal cannot use is
 * absent, not greyed out. Below 900px the sidebar becomes an off-canvas drawer.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { session, brands, principal, brandId, setBrandId } = useSession();
  const [navOpen, setNavOpen] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  /**
   * `navOpen` says the drawer is out. `modal` says it is ACTING as a dialog —
   * containing focus, hiding the background from assistive technology, and
   * taking the keyboard. They are not the same state and conflating them is
   * the bug this guards against: above 900px the nav is a permanent sidebar
   * and trapping focus in it would trap the operator in their own navigation,
   * with no toggle on screen to get out by.
   */
  const [modal, setModal] = useState(false);
  /**
   * Set when Escape or the menu's close control closes the drawer, so focus
   * goes back to the toggle — and ONLY then. A nav link closes the drawer by
   * navigating, and dragging focus back to the toggle after a navigation
   * would take it off the page the person just asked for; the scrim is a
   * pointer action and has no focus to give back.
   */
  const restoreFocusRef = useRef(false);

  /**
   * THE DRAWER IS MODAL EXACTLY WHEN THE TOGGLE IS ON SCREEN, and that is read
   * from the toggle rather than from a breakpoint.
   *
   * `components.css` turns the sidebar into an off-canvas drawer at
   * `max-width: 900px` and shows `.nav-toggle` in the same query. Writing 900
   * again here would be a second copy of one decision, in a language that
   * cannot see the first — and the two would be free to drift, which is the
   * defect I have spent the week finding in other forms. Asking the browser
   * what the toggle's computed display is asks the stylesheet itself, so there
   * is one breakpoint and it lives in the CSS.
   *
   * It is also the honest test: if a person can see the control that opens the
   * drawer, the drawer is a drawer. If they cannot, it is the sidebar.
   */
  const drawerIsModal = useCallback(() => {
    const toggle = navToggleRef.current;
    if (!toggle) return false;
    try {
      return getComputedStyle(toggle).display !== 'none';
    } catch {
      return false;
    }
  }, []);

  /**
   * The drawer is a MODAL DIALOG while it is out, and this is the half that
   * makes that true for a keyboard.
   *
   * The scrim has always promised modality — it is what tells a sighted user
   * the page behind is out of play — and the product kept that promise for
   * pointers only. The scrim blocks clicks; it does not block focus. So Tab
   * walked straight out of the open drawer into controls that were visually
   * obscured, could not be clicked, and gave no feedback at all. Nothing said
   * `aria-modal`, so a screen-reader user was never told any of it.
   *
   * The decision that settled it: the interface already makes the claim, so
   * make the behaviour match the claim rather than deleting the claim.
   *
   * Escape closes the drawer and gives the keyboard its place back.
   *
   * THE DRAWER HAD NO KEY HANDLER AT ALL. It could be dismissed by clicking the
   * scrim — a bare <div> with no accessible name, so not a control a keyboard
   * or switch user can reach — or by activating a nav link, WHICH DISMISSES IT
   * BY NAVIGATING AWAY. There was no way to open it, look, and back out.
   *
   * `AccountMenu` two hundred lines above already does this, and its comment
   * says Escape is "the only way a keyboard user gets their place back". This
   * is the same pattern with the missing half restored, not a second one.
   *
   * The listener is on the DOCUMENT rather than on the <nav>, because the
   * drawer never takes focus when it opens: right after the toggle is pressed
   * focus is still on the toggle, and after a pointer tap it may be on <body>.
   * A handler bound to the drawer would miss both — the two commonest states
   * the key is pressed in. The document listener is also what AccountMenu uses
   * for its own outside-dismissal, so it is the established shape in this file.
   *
   * Focus returns to the toggle on the way out. Without that, dismissing the
   * drawer strands focus wherever it was — on an element the same state change
   * has just moved off screen.
   */
  useEffect(() => {
    if (!navOpen) {
      setModal(false);
      return;
    }

    /* Opened while the toggle is off screen — the nav is the permanent
       sidebar, so there is nothing to contain and nothing to escape from.
       This is reachable: open the drawer on a phone-width window and widen
       it. The drawer state is dropped rather than left true and inert. */
    if (!drawerIsModal()) {
      setNavOpen(false);
      return;
    }
    setModal(true);

    const nav = navRef.current;

    /* FOCUS GOES IN. Until this branch it did not, and the drawer's links sit
       BEFORE the toggle in the DOM, so a keyboard user had to Shift+Tab
       BACKWARDS to reach what they had just opened. */
    focusables(nav)[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      /* WHILE A DIALOG IS OPEN OVER THE MENU, THE MENU TAKES NO KEYS. A
         dialog can open while the menu is open — the error dialog does, when
         a read fails — and it is drawn above the menu and portalled to
         <body>, outside everything made inert behind the menu. Its trap only
         wraps at its own ends, so the wrap below, which brings focus in from
         anywhere outside the menu, took every Tab pressed inside the dialog
         to the menu hidden behind it. The top layer has the keyboard; the
         menu's handler stands down until it closes. A page drawer under the
         menu is a modal layer too, but it is inert there and does not count:
         Escape over it still closes the menu. */
      const dialogAbove = Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
        (layer) => layer !== nav && !layer.closest('[inert]'),
      );
      if (dialogAbove) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        restoreFocusRef.current = true;
        setNavOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !nav) return;

      /* THE WRAP. `inert` on the background stops focus LEAVING into the page,
         but it does not bring focus back round — without this, Tab off the
         last link walks out to the browser's own UI and the drawer is a
         one-way trip.

         FROM OUTSIDE THE DRAWER, BOTH KEYS LEAD BACK IN. The menu button is
         the one control behind the drawer that is not inert (see the
         containment comment at <main>), and nothing after it in the page can
         take focus — so a forward Tab from it would leave for the browser's
         own UI. Shift+Tab already came back in; Tab now does too. Never while
         a dialog is open over the menu: the guard at the top of this handler
         has already left those keys to the dialog. */
      const items = focusables(nav);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !nav.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !nav.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    /* On the DOCUMENT, not on the <nav>. With focus entry above, focus is
       normally inside the drawer and a scoped listener would do — but only
       normally. A listener on the document holds when something else has
       taken focus, and it is what `AccountMenu` in this file already uses.
       The walk can no longer DISTINGUISH the two, and says so rather than
       keeping an assertion that passes for a reason that no longer exists. */
    document.addEventListener('keydown', onKeyDown);

    /* Widening the window past the breakpoint stops it being a drawer. Closing
       is the whole response: above 900px `data-open` changes nothing visible,
       so the state is meaningless there, and leaving it true would leave a
       trap armed around a sidebar. */
    const onResize = () => {
      if (!drawerIsModal()) setNavOpen(false);
    };
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [navOpen, drawerIsModal]);

  /**
   * THE RESTORE RUNS IN ITS OWN EFFECT, once the drawer has closed, and the
   * reason is the bug the containment branch created and caught.
   *
   * When containment landed, the toggle was inside the `<main>` it marked
   * `inert` while the drawer was modal. Calling `.focus()` on an element in an
   * inert subtree is a NO-OP — it does not throw, it does not warn, it simply
   * does nothing. So the obvious arrangement, focusing the toggle in the same
   * handler that closes the drawer, silently stopped working the moment
   * containment landed: focus stayed on the nav link and the operator was left
   * on a control that had just slid off screen.
   *
   * THE CONTAINMENT FIX BROKE THE EXIT FIX, and nothing failed loudly. The
   * walk is the only reason it was caught — `focus returned to toggle: false`
   * against an otherwise green run.
   *
   * The same no-op later kept the menu button from closing the menu (the
   * containment comment at `<main>` has that story), and the toggle is now
   * the one control behind the drawer that is never inert. This restore no
   * longer has to wait for `inert` to come off; it stays where it is, after
   * the render that closes the drawer.
   */
  useEffect(() => {
    if (modal || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    navToggleRef.current?.focus();
  }, [modal]);

  if (!session || !principal) return null;
  const groups = visibleNav(principal);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {/* CLOSES ON MOUSEDOWN, NOT CLICK, because on WebKit — every iPhone — a
          tap on this scrim produces mousedown and mouseup and NO click once
          the page behind it has been scrolled at all (reproduced at 600px and
          1272px; Chromium delivers the click at every offset). With `onClick`
          a phone user who scrolled, opened the menu and tapped outside it was
          stuck: no Escape key, and the toggle is under this very scrim. The
          shared Drawer and Dialog backdrops already close on mousedown; this
          is the same shape. Found by the phone-width control sweep. */}
      {navOpen && <div className="nav-scrim" onMouseDown={() => setNavOpen(false)} />}

      {/* `role="dialog"` REPLACES the navigation landmark while the drawer is
          modal, deliberately: a landmark invites a screen-reader user to jump
          in and out of it, and while this is modal there is no out. It goes
          back to being a <nav> the moment it is a sidebar again. */}
      <nav
        ref={navRef}
        className="shell-nav"
        data-open={navOpen}
        aria-label="Console sections"
        {...(modal ? ({ role: 'dialog', 'aria-modal': true } as const) : {})}
      >
        {/* THE MENU BUTTON'S PLACE, WHILE THE MENU IS OPEN. The open menu
            covers its own button, and the header that holds the button is
            drawn below the menu, so a finger pressing it again landed on the
            brand link and was taken to the dashboard. This sits exactly where
            the button is, over the menu (components.css places it with the
            header's own spacing), and is shown only while the menu is open at
            phone width. It is first in the menu, so focus starts here: Enter
            to open, Enter to close. Focus goes back to the menu button, as it
            does after Escape. */}
        <Button
          iconOnly
          className="nav-close"
          aria-label="Close navigation"
          onClick={() => {
            restoreFocusRef.current = true;
            setNavOpen(false);
          }}
        >
          <Icon name="x" />
        </Button>

        {/* Closes the drawer, like every link below it. This one was missed,
            and it was the link the drawer focused first: Enter straight after
            opening the menu took the operator to the dashboard with the menu
            still open over it.

            It leads to the first module this reader's sidebar offers: the
            dashboard for a role holding players.view, Reports for the PAGCOR
            Regulator, for which the dashboard would only be a refusal. */}
        <NavLink
          to={homePath(principal)}
          className="nav-brand"
          onClick={() => setNavOpen(false)}
        >
          <BrandMark size={30}>
            <span>
              NorthAlley<span style={{ color: 'var(--portal-accent-text)' }}>OPS</span>
            </span>
          </BrandMark>
        </NavLink>

        <div className="nav-groups">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="nav-group-label">{group.label}</div>
              <div className="nav-list">
                {group.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.path === '/'}
                    className="nav-item"
                    // Closed from the click that navigates, not from an effect
                    // watching the pathname. Leaving the drawer open over the
                    // new page is the classic mobile-nav bug.
                    onClick={() => setNavOpen(false)}
                  >
                    <Icon name={item.icon} className="nav-icon" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* `inert` is the containment. It takes the background out of the tab
          order AND out of the accessibility tree, which is what makes the
          account menu unreachable while the drawer is open — and that is what
          closes the double-Escape defect without anybody writing a
          `stopPropagation()` that would be dead code the day after containment
          shipped. It also stands down whatever the screen itself has open: a
          page drawer is drawn inside the page body, so while the menu is over
          it, it takes neither focus nor a tap.

          EVERYTHING BEHIND THE DRAWER IS INERT EXCEPT THE MENU BUTTON, and the
          exception is the fix for a phone menu that would not close. This
          attribute sat on <main> itself, and the menu button is inside <main>:
          while the menu was open its own button could not take focus, so Enter
          "on the menu button" landed on whatever the drawer had focused — its
          first link, the brand, which took the operator to the dashboard with
          the menu still open over it. CI's phone sweep found that on four
          screens on 2026-10-15 (run 38674474368), where its tap on the scrim
          had not landed within its two seconds; every screen was exposed. So
          the attribute is on each part of <main> but the button: the rest of
          the header, and the page. The button only toggles, and a Tab from it
          leads back into the drawer (the wrap above). */}
      <main className="shell-main">
        <header className="shell-header">
          <Button
            ref={navToggleRef}
            iconOnly
            className="nav-toggle"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon name="list" />
          </Button>

          {/* Brand scope: global for the session, not per widget. */}
          <div className="row" style={{ gap: 'var(--space-2)' }} inert={modal}>
            <span className="brand-label text-subtle">Brand</span>
            <div className="seg">
              <label className="seg-opt">
                <input
                  type="radio"
                  name="brand"
                  checked={brandId === null}
                  onChange={() => setBrandId(null)}
                />
                {/* "All 1" said nothing to an admin held to one brand (module walk, 30 Oct
                    2026): the count is shown only when there is more than one to count. */}
                All{brands.length > 1 ? ` ${brands.length}` : ''}
              </label>
              {brands.map((b) => (
                <label className="seg-opt" key={b.id}>
                  <input
                    type="radio"
                    name="brand"
                    checked={brandId === b.id}
                    onChange={() => setBrandId(b.id)}
                  />
                  {b.name}
                </label>
              ))}
              {/* A CHOSEN BRAND WHOSE NAME HAS NOT ARRIVED. The names come in a read after the
                  session's, and until then (or for good, if that read fails) the chosen brand had
                  no option of its own here, so the header read as "All" while every screen read
                  that one brand. It is shown chosen, as "Selected brand" — the screens' own word
                  for it, "the selected brand" — until its name arrives. */}
              {brandId !== null && !brands.some((b) => b.id === brandId) && (
                <label className="seg-opt">
                  <input type="radio" name="brand" checked onChange={() => {}} />
                  Selected brand
                </label>
              )}
            </div>
          </div>

          <div className="header-spacer row" style={{ gap: 'var(--space-3)' }} inert={modal}>
            <ThemeToggle storageKey="dayon-back-office-theme" />
            {/* Sign-out lives in here now. It was an unlabelled icon beside an
                inert identity block, and the account-wide sign-out the API has
                always offered — and points operators at by name — had no
                screen at all. */}
            <AccountMenu />
          </div>
        </header>

        <div id="main" className="shell-body" inert={modal}>
          {children}
        </div>
      </main>
    </div>
  );
}
